import React, { useState, useEffect, useRef } from 'react';
import { bookings } from '../api';
import PhoneInput from 'react-phone-number-input';
import DateSelector from '../components/Calendar/DateSelector';
import 'react-phone-number-input/style.css';
import '../pages/PublicGuestBooking.css';
import LessonPrices from '../components/Bookings/LessonPrices';

function PublicGuestBooking({ coachId, onClose }) {
  const [coach, setCoach] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    partySize: '',
    playerNames: ['', '', '', ''],
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const groupSizeSectionRef = useRef(null);

  // Smooth scroll when slot is selected
  useEffect(() => {
    if (selectedSlot && groupSizeSectionRef.current) {
      setTimeout(() => {
        const element = groupSizeSectionRef.current;
        const targetPosition = element.getBoundingClientRect().top + window.scrollY - 100;
        const startPosition = window.scrollY;
        const distance = targetPosition - startPosition;
        const duration = 1200; // 1.2 seconds for slower scroll
        let start = null;

        const smoothScroll = (timestamp) => {
          if (!start) start = timestamp;
          const progress = (timestamp - start) / duration;

          if (progress < 1) {
            // Easing function for smooth acceleration/deceleration
            const ease = progress < 0.5 ? 2 * progress * progress : -1 + 4 * progress - 2 * progress * progress;
            window.scrollTo(0, startPosition + distance * ease);
            requestAnimationFrame(smoothScroll);
          } else {
            window.scrollTo(0, targetPosition);
          }
        };

        requestAnimationFrame(smoothScroll);
      }, 100);
    }
  }, [selectedSlot]);

  // Load coach and slots
  useEffect(() => {
    const loadCoach = async () => {
      try {
        setLoading(true);
        const response = await bookings.validateCoach(coachId);
        setCoach(response.data.coach);

        if (response.data.hasAvailableSlots) {
          // Load available slots for this coach (90 days forward)
          const now = new Date();
          const ninetyDaysFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

          const slotsResponse = await bookings.browseSlots(
            1,
            100,
            coachId,
            now.toISOString(),
            ninetyDaysFromNow.toISOString()
          );

          // Filter to future slots only. Duplicate/overlapping slots used to
          // slip through from the backend (two different buffer calendar
          // events covering the same time), worked around here by
          // deduping on startTime - that's now fixed at the source
          // (CalendarBufferService dedupes on real interval overlap, not
          // just exact start times), and this page and BrowseCoachingTab
          // (the logged-in player's equivalent) both read the same
          // /bookings/browse/all data, so keeping a startTime-only
          // workaround here made this page's behavior diverge from that
          // one instead of matching it.
          const futureSlots = slotsResponse.data.slots
            .filter(slot => new Date(slot.startTime) > new Date());

          setSlots(futureSlots);
        }

        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error loading coach information');
        setLoading(false);
      }
    };

    loadCoach();
  }, [coachId]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlayerNameChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      playerNames: prev.playerNames.map((name, i) => i === index ? value : name)
    }));
  };

  // Get max group size based on lesson duration
  const getMaxGroupSizeForDuration = () => {
    if (!selectedSlot) return 4; // Default max if no slot selected

    const durationMinutes = (new Date(selectedSlot.endTime) - new Date(selectedSlot.startTime)) / (1000 * 60);

    if (durationMinutes >= 90) {
      return 4; // 90+ min lessons: max 4 people
    } else if (durationMinutes >= 60) {
      return 2; // 60 min lessons: max 2 people
    }
    return 4; // Default
  };

  // Get max party size based on selected slot capacity and duration
  const getMaxPartySizeForSlot = () => {
    if (!selectedSlot) return 4; // Default max if no slot selected

    const spotsAvailable = Math.max(0, selectedSlot.maxBookings - selectedSlot.currentBookings);
    const maxByDuration = getMaxGroupSizeForDuration();

    return Math.min(maxByDuration, spotsAvailable); // Cap at duration limit or available spots
  };

  const maxPartySize = getMaxPartySizeForSlot();

  // Remove duplicates and exclude full slots (for calendar highlighting only)
  const getAvailableSlots = () => {
    const seenTimes = new Set();
    const filtered = slots.filter(slot => {
      // Check how many spots are available
      const spotsAvailable = slot.capacityInfo?.available ?? Math.max(0, slot.maxBookings - slot.currentBookings);

      // Skip full slots - only show days with actually available spots for calendar
      if (spotsAvailable === 0) {
        return false;
      }

      // Skip if we've already seen a slot with this start time
      const timeKey = new Date(slot.startTime).toISOString();
      if (seenTimes.has(timeKey)) {
        return false;
      }
      seenTimes.add(timeKey);
      return true;
    });

    return filtered;
  };

  const availableSlots = getAvailableSlots();

  // Get slots for selected date only (in user's local timezone)
  const getSlotsForDate = (date) => {
    if (!date) return [];

    // Get local date string (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const slotsForThisDate = availableSlots.filter(slot => {
      const slotDate = new Date(slot.startTime);
      const slotYear = slotDate.getFullYear();
      const slotMonth = String(slotDate.getMonth() + 1).padStart(2, '0');
      const slotDay = String(slotDate.getDate()).padStart(2, '0');
      const slotDateStr = `${slotYear}-${slotMonth}-${slotDay}`;

      const match = slotDateStr === dateStr;
      if (match) {
      }
      return match;
    });

    return slotsForThisDate;
  };

  const slotsForSelectedDate = selectedDate ? getSlotsForDate(selectedDate) : [];

  // Calculate price based on party size and duration
  const calculatePrice = (partySize, startTime, endTime) => {
    const durationMinutes = (new Date(endTime) - new Date(startTime)) / (1000 * 60);
    let price = 0;

    // Base price by duration
    if (durationMinutes >= 120) {
      price = 400;
    } else if (durationMinutes >= 90) {
      price = 300;
    } else {
      // Default pricing by party size
      if (partySize === 1) {
        price = 200;
      } else if (partySize === 2) {
        price = 220;
      } else {
        price = 220 + (partySize - 2) * 10;
      }
    }

    return price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSlot) {
      setError('Please select a slot');
      return;
    }

    // Validate form
    if (!formData.name || !formData.email || !formData.phone || !formData.partySize) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate player names based on party size
    const partySize = parseInt(formData.partySize);
    for (let i = 0; i < partySize; i++) {
      if (!formData.playerNames[i] || formData.playerNames[i].trim() === '') {
        setError(`Please enter name for player ${i + 1}`);
        return;
      }
    }

    try {
      setSubmitting(true);

      if (!formData.phone) {
        setError('Please enter a valid phone number');
        setSubmitting(false);
        return;
      }

      // Extract country code from E.164 format phone number
      // E.164 format: +{country_code}{number}
      // Example: +14155552671 → countryCode: "+1", phone: "4155552671"
      const phoneMatch = formData.phone.match(/^\+(\d{1,3})([\d\s\-\(\)]+)$/);
      if (!phoneMatch) {
        setError('Invalid phone number format');
        setSubmitting(false);
        return;
      }

      const countryCode = '+' + phoneMatch[1];
      const phoneNumber = phoneMatch[2].replace(/[\s\-\(\)]/g, ''); // Remove formatting

      const partySize = parseInt(formData.partySize);
      const payload = {
        slotId: selectedSlot._id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone, // Full E.164 format
        countryCode: countryCode,
        partySize: partySize,
        playerNames: formData.playerNames.slice(0, partySize),
        message: formData.message || null
      };

      const response = await bookings.createGuestBooking(payload);

      if (response.data.success) {
        setConfirmation(response.data.booking);

        // Remove the booked slot from the available slots list
        // This prevents the slot from showing as available after booking
        setSlots(prevSlots =>
          prevSlots.filter(slot => slot._id !== selectedSlot._id)
        );

        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="guest-booking-container">
        <div className="loading-state">Loading coach information...</div>
      </div>
    );
  }

  if (error && !confirmation) {
    return (
      <div className="guest-booking-container">
        <div className="error-state">{error}</div>
        {onClose && (
          <button onClick={onClose} className="close-btn">Go Back</button>
        )}
      </div>
    );
  }

  if (confirmation) {
    const price = calculatePrice(
      confirmation.partySize,
      confirmation.scheduledStartTime,
      confirmation.scheduledEndTime
    );

    // Check if this is a second booking (using email key in localStorage)
    const bookingKey = `booking_${formData.email}`;
    const previousBooking = localStorage.getItem(bookingKey);
    const isSecondBooking = !!previousBooking;

    // Save current booking to localStorage
    localStorage.setItem(bookingKey, JSON.stringify({
      bookingId: confirmation._id,
      timestamp: new Date().toISOString()
    }));

    return (
      <div className="guest-booking-container">
        <div className="confirmation-modal">
          <div className="confirmation-header">✅ Booking Request Submitted!</div>

          <div className="status-indicator">
            <div className="status-badge pending">⏳ Pending Coach Approval</div>
            <p className="status-message">
              Your booking request has been sent to {confirmation.coachName}.
              Please wait for the coach to review and approve your request.
            </p>
          </div>

          <div className="confirmation-details">
            <div className="detail-row">
              <span className="label">Date & Time:</span>
              <span className="value">
                {new Date(confirmation.scheduledStartTime).toLocaleDateString()} {' '}
                {new Date(confirmation.scheduledStartTime).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>

            <div className="detail-row">
              <span className="label">Session:</span>
              <span className="value">{confirmation.slotTitle}</span>
            </div>

            <div className="detail-row">
              <span className="label">Party Size:</span>
              <span className="value">{confirmation.partySize} {confirmation.partySize === 1 ? 'person' : 'people'}</span>
            </div>

            {confirmation.playerNames && confirmation.playerNames.length > 0 && (
              <div className="detail-row">
                <span className="label">Players:</span>
                <span className="value">
                  {confirmation.playerNames.map((name, idx) => (
                    <div key={idx} style={{ marginBottom: idx < confirmation.playerNames.length - 1 ? '6px' : '0' }}>
                      {idx + 1}. {name}
                    </div>
                  ))}
                </span>
              </div>
            )}

            <div className="detail-row">
              <span className="label">Coach:</span>
              <span className="value">{confirmation.coachName}</span>
            </div>

            <div className="detail-row">
              <span className="label">Price:</span>
              <span className="value" style={{ fontWeight: '700', color: '#27ae60' }}>${price}</span>
            </div>

            <div className="detail-row">
              <span className="label">Confirmation #:</span>
              <span className="value">{confirmation._id.substring(0, 8).toUpperCase()}</span>
            </div>
          </div>

          <button
            onClick={() => {
              navigator.clipboard.writeText(
                `Booking Details:\nDate & Time: ${new Date(confirmation.scheduledStartTime).toLocaleString()}\nSession: ${confirmation.slotTitle}\nParty Size: ${confirmation.partySize}\nCoach: ${confirmation.coachName}\nPrice: $${price}\nConfirmation: ${confirmation._id.substring(0, 8).toUpperCase()}`
              );
              alert('Booking details copied to clipboard!');
            }}
            className="action-btn copy-btn"
          >
            📋 Copy Details
          </button>

          <div className="approval-info">
            <h4>What Happens Next?</h4>
            <ul>
              <li>✅ Your booking request has been sent to {confirmation.coachName}</li>
              <li>🔔 The coach will receive an app notification about your booking</li>
              <li>⏳ The coach will review your request and approve or deny within 24 hours</li>
              <li>📱 You will receive an app notification once the coach responds to your booking</li>
              <li>❌ You can cancel this booking request before the coach approves</li>
            </ul>
          </div>

          {isSecondBooking && (confirmation.partySize === 1 || confirmation.partySize === 2) && (
            <div
              onClick={() => setShowOfferModal(true)}
              style={{
                background: '#fff3cd',
                border: '2px solid #ffc107',
                borderRadius: '8px',
                padding: '15px',
                marginTop: '15px',
                fontSize: '13px',
                lineHeight: '1.6',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                transform: 'scale(1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#856404' }}>
                💎 Special Offer Available! (Tap to learn more)
              </div>
              <p style={{ margin: '0', color: '#856404' }}>
                Purchase a coaching pack of <strong>10 lessons</strong> and get <strong>10% discount</strong>!
                Great value for regular coaching.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={async () => {
                if (window.confirm('Cancel this booking request? You can make a new booking anytime.')) {
                  try {
                    setSubmitting(true);
                    await bookings.cancelPendingBooking(confirmation._id);
                    setConfirmation(null);
                    setError(null);
                    if (onClose) onClose();
                  } catch (err) {
                    setError(err.response?.data?.message || 'Error cancelling booking');
                  } finally {
                    setSubmitting(false);
                  }
                }
              }}
              disabled={submitting}
              className="action-btn"
              style={{
                background: '#f44336',
                color: 'white',
                border: 'none',
                flex: 1
              }}
            >
              ❌ Cancel Booking
            </button>
            {onClose && (
              <button onClick={onClose} className="close-btn" style={{ flex: 1 }}>Close</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-booking-container">
      <div className="booking-header">
        <h1>🎾 Book a Coaching Session</h1>
        <p className="coach-name">with {coach?.name}</p>
      </div>

      <LessonPrices compact />

      <form onSubmit={handleSubmit} className="booking-form">
        {/* Calendar & Slot Selection */}
        <div className="form-section">
          <h3>Select a Date & Slot</h3>
          {slots.length === 0 ? (
            <div className="no-slots">No available slots at this time.</div>
          ) : (
            <>
              <DateSelector
                slots={availableSlots}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                partySize={1}
              />

              {selectedDate && (
                <div className="slots-for-date">
                  <h4>Available on {selectedDate.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}</h4>
                  {slotsForSelectedDate.length === 0 ? (
                    <div className="no-slots-for-date">
                      No slots available on this date.
                    </div>
                  ) : (
                    <div className="slots-grid">
                      {slotsForSelectedDate.map(slot => {
                        const spotsAvailable = Math.max(0, slot.maxBookings - slot.currentBookings);
                        const durationMinutes = (new Date(slot.endTime) - new Date(slot.startTime)) / (1000 * 60);
                        return (
                          <div
                            key={slot._id}
                            className={`slot-card ${selectedSlot?._id === slot._id ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedSlot(slot);
                              setFormData(prev => ({ ...prev, partySize: '' })); // Reset party size when slot changes
                            }}
                          >
                            <div className="slot-time">
                              {new Date(slot.startTime).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <div className="slot-title">{slot.title}</div>
                            <div className="slot-duration" style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                              {durationMinutes} mins
                            </div>
                            <div className="slot-available">
                              {spotsAvailable} {spotsAvailable === 1 ? 'spot' : 'spots'} available
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {!selectedDate && availableSlots.length > 0 && (
                <div className="select-date-prompt">
                  👆 Tap a date to see available slots
                </div>
              )}
            </>
          )}
        </div>

        {/* Group Size Selection - Only show after slot is selected */}
        {selectedSlot && (
          <div ref={groupSizeSectionRef} className="form-section" style={{ background: '#f8f9ff', padding: '20px', borderRadius: '12px', border: '1px solid #2f4ba2' }}>
            <h3 style={{ color: '#2f4ba2', marginTop: 0 }}>How many people are joining?</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
              Selected: <strong>{selectedSlot.title}</strong> at <strong>{new Date(selectedSlot.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
            </p>
            <select
              name="partySize"
              value={formData.partySize}
              onChange={(e) => {
                handleFormChange(e);
                // Reset player names when party size changes
                setFormData(prev => ({
                  ...prev,
                  playerNames: ['', '', '', '']
                }));
              }}
              required
              style={{ marginBottom: '15px', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
            >
              <option value="">Select number of people *</option>
              {(() => {
                const durationMinutes = (new Date(selectedSlot.endTime) - new Date(selectedSlot.startTime)) / (1000 * 60);

                // Determine max based on duration only
                let maxByDuration;
                if (durationMinutes >= 90) {
                  maxByDuration = 4; // 90+ min: max 4 people
                } else if (durationMinutes >= 60) {
                  maxByDuration = 2; // 60 min: max 2 people
                } else {
                  maxByDuration = 4; // Default
                }

                // Create array with valid options based on duration only
                return [...Array(maxByDuration)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1} {i === 0 ? 'person' : 'people'}</option>
                ));
              })()}
            </select>
            <p style={{ fontSize: '11px', color: '#2f4ba2', margin: '5px 0 0', fontWeight: '600' }}>
              {(() => {
                const durationMinutes = (new Date(selectedSlot.endTime) - new Date(selectedSlot.startTime)) / (1000 * 60);
                return durationMinutes >= 90
                  ? '💡 90+ minute lesson: 1-4 people'
                  : '💡 60-minute lesson: 1-2 people';
              })()}
            </p>
          </div>
        )}

        {/* Player Names - Show after party size is selected */}
        {formData.partySize && (
          <div className="form-section" style={{ background: '#f8f9ff', padding: '20px', borderRadius: '12px', border: '1px solid #2f4ba2' }}>
            <h3 style={{ marginTop: 0, color: '#2f4ba2' }}>Player Names</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
              Enter the names of all {parseInt(formData.partySize)} {formData.partySize === '1' ? 'player' : 'players'} joining this session
            </p>
            {[...Array(parseInt(formData.partySize))].map((_, i) => (
              <div key={i} className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: '600', color: '#333' }}>Player {i + 1} Name *</label>
                <input
                  type="text"
                  value={formData.playerNames[i]}
                  onChange={(e) => handlePlayerNameChange(i, e.target.value)}
                  placeholder={`Player ${i + 1} full name`}
                  required
                  style={{ marginBottom: '0' }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Guest Information (Organizer Contact) */}
        <div className="form-section" style={{ background: '#f8f9ff', padding: '20px', borderRadius: '12px', border: '1px solid #2f4ba2' }}>
          <h3 style={{ color: '#2f4ba2', marginTop: 0 }}>Your Information</h3>

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              placeholder="Your full name"
              required
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleFormChange}
              placeholder="yourplaytomic@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Phone Number *</label>
            <PhoneInput
              defaultCountry="SG"
              value={formData.phone}
              onChange={(phone) => setFormData(prev => ({ ...prev, phone: phone || '' }))}
              placeholder="Enter phone number"
              international
              countryCallingCodeEditable
              required
            />
          </div>

          <div className="form-group">
            <label>Message (Optional)</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleFormChange}
              placeholder="Any special requests or questions..."
              rows="3"
            />
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          disabled={submitting || !selectedSlot}
          className="submit-btn"
        >
          {submitting ? 'Booking...' : '✅ Confirm Booking'}
        </button>
      </form>

      {onClose && (
        <button onClick={onClose} className="cancel-btn">Cancel</button>
      )}

      {/* Special Offer Terms & Conditions Modal */}
      {showOfferModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              color: '#2c3e50',
              marginTop: 0,
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              💎 10-Lesson Coaching Pack
            </h2>

            <div style={{
              background: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '10px' }}>
                🎉 10% DISCOUNT
              </div>
              <div style={{
                color: '#856404',
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                Save on your coaching lessons
              </div>
            </div>

            <div style={{
              lineHeight: '1.8',
              color: '#333',
              fontSize: '14px'
            }}>
              <h4 style={{ color: '#2f4ba2', marginTop: 0 }}>Pricing Breakdown:</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Single Player (60 min):</strong> ~~$200~~ → <strong>$180</strong> per lesson</li>
                <li><strong>Two Players (60 min):</strong> ~~$220~~ → <strong>$198</strong> per lesson</li>
                <li><strong>10 Lessons Bundle:</strong> Buy all at once for the discount</li>
              </ul>

              <h4 style={{ color: '#2f4ba2' }}>Terms & Conditions:</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Validity:</strong> 6-month window to use all 10 lessons. After 6 months, expired lessons cannot be used.</li>
                <li><strong>Non-Transferable:</strong> Lessons are personal to your account and cannot be transferred to another person.</li>
                <li><strong>No Duration Changes:</strong> Lesson length cannot be altered. 60-minute lessons must be used as 60 minutes.</li>
                <li><strong>No Sharing:</strong> One lesson slot = one booking. Cannot split a lesson between multiple people.</li>
                <li><strong>Non-Refundable:</strong> Pack payment is non-refundable once purchased.</li>
                <li><strong>Cannot Combine:</strong> Offer cannot be combined with other discounts or promotions.</li>
                <li><strong>Extensions:</strong> Extensions beyond the 6-month window may be granted upon request if causal necessity is demonstrated and coach approves.</li>
              </ul>

              <h4 style={{ color: '#2f4ba2' }}>Example:</h4>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Purchase 10 lessons at $180 each = $1,800 total (saves $200)</li>
                <li>Book and complete lessons within 6 months</li>
                <li>Each lesson slots one person or group for full duration</li>
              </ul>

              <div style={{
                background: '#e3f2fd',
                border: '1px solid #2196f3',
                borderRadius: '6px',
                padding: '12px',
                marginTop: '20px',
                fontSize: '12px',
                color: '#1565c0'
              }}>
                <strong>✅ How to Get Started:</strong> After booking your first lesson, contact your coach to purchase a 10-lesson pack at the discounted rate.
              </div>
            </div>

            <button
              onClick={() => setShowOfferModal(false)}
              style={{
                marginTop: '20px',
                width: '100%',
                padding: '12px',
                background: '#2f4ba2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Got It! 👍
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicGuestBooking;
